/**
 * NavbarMobileMenu — the right-side Sheet drawer for mobile.
 * Renders the flat nav links, the category search list, the signed-in
 * quick-links, and the sign-in / sign-out footer.
 */
import { Link, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, Store, Package, User, Settings, LogOut, Tag } from "lucide-react";
import NavbarCategorySearch from "@/components/NavbarCategorySearch";

export default function NavbarMobileMenu({
  open,
  onOpenChange,
  mobileLinks,
  categories,
  user,
  onLogout,
  onOpenAuth,
}) {
  const location = useLocation();
  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild className="md:hidden">
        <button className="p-2 text-gray-600" data-testid="mobile-menu-trigger">
          <Menu className="w-6 h-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[280px]">
        <div className="flex flex-col h-full py-6">
          <div className="flex items-center justify-between mb-8">
            <img src="/logo.png" alt="The Blk Community" className="h-9 w-auto" />
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto">
            {mobileLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={close}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  location.pathname === link.href
                    ? "bg-tbc-cream text-tbc-navy"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {categories.length > 0 && (
              <div className="pt-2">
                <div className="px-4 pt-3 pb-2 text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5" />
                  Search by category
                </div>
                <div className="space-y-1" data-testid="mobile-nav-categories">
                  <NavbarCategorySearch categories={categories} variant="mobile" onNavigate={close} />
                </div>
              </div>
            )}

            {user && (
              <>
                <Link
                  to="/dashboard"
                  onClick={close}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  <Store className="w-5 h-5" />
                  Dashboard
                </Link>
                <Link
                  to="/orders"
                  onClick={close}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  <Package className="w-5 h-5" />
                  Orders
                </Link>
                <Link
                  to="/profile"
                  onClick={close}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  <User className="w-5 h-5" />
                  Profile
                </Link>
                <Link
                  to="/admin"
                  onClick={close}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  <Settings className="w-5 h-5" />
                  Admin Panel
                </Link>
              </>
            )}
          </div>

          <div className="pt-6 border-t border-gray-100">
            {user ? (
              <button
                onClick={() => {
                  onLogout();
                  close();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            ) : (
              <Button
                onClick={() => {
                  close();
                  onOpenAuth();
                }}
                className="w-full bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full"
                data-testid="mobile-login-button"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
